import React, { useEffect, useState } from "react";
import { Select, DatePicker, Row, Col, TimeRangePickerProps } from "antd";
import "./Overview.css";
import dayjs, { Dayjs } from "dayjs";
import { SmallStatCard } from "cms/components/SmallStatCard/SmallStatCard";
import { LargeStatCard } from "../LargeStatCard/LargeStatCard";
import { ActivityCard } from "cms/components/ActivityCard/ActivityCard";

import CheckCircle from "assets/CheckCircle.svg";
import User from "assets/User.svg";
import Vector from "assets/Vector.svg";
import FieldTime from "assets/FieldTime.svg";
import { useList } from "@refinedev/core";
import { CalendarOutlined } from "@ant-design/icons";

const { Option } = Select;
const { RangePicker } = DatePicker;

const subjectsOptions = [
  "AS Biology",
  "AS Chemistry",
  "IG Physics",
  "IG Mathematics",
  "All subjects",
];
const timePeriodOptions = [
  "Last 7 days",
  "Last 14 days",
  "Last 30 days",
  "Custom",
];

export const Overview: React.FC = () => {
  const [selectedSubject, setSelectedSubject] = useState<string>("All subjects");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("Last 7 days");
  const [customDateRange, setCustomDateRange] = useState<
    null | [start: Dayjs | null | undefined, end: Dayjs | null | undefined]
  >([null, null]);

  const [logs, setLogs] = useState<any[]>([]);

  const { data: logs_raw } = useList({
    resource: "cms/admin/dashboard/activity",
    liveMode: "auto"
  });

  const log_data = logs_raw?.data as unknown as any[];

  useEffect(() => {
    if (log_data) {
      setLogs(log_data);
    }
  }, [log_data]);

  const staticLogs = [
    {
      timestamp: dayjs().hour(10).minute(30).format("YYYY-MM-DD HH:mm:ss"),
      text: "User A completed task 1.",
    },
    {
      timestamp: dayjs().hour(11).minute(0).format("YYYY-MM-DD HH:mm:ss"),
      text: "User B uploaded a file.",
    },
    {
      timestamp: dayjs().hour(12).minute(15).format("YYYY-MM-DD HH:mm:ss"),
      text: "User C commented on a post.",
    },
    {
      timestamp: dayjs().hour(13).minute(45).format("YYYY-MM-DD HH:mm:ss"),
      text: "User D created a new task.",
    },
    {
      timestamp: dayjs().hour(15).minute(30).format("YYYY-MM-DD HH:mm:ss"),
      text: "User E completed task 2.",
    },
  ];

  const [filteredData, setFilteredData] = useState({
    completedItems: 55,
    hoursCompleted: "40",
    overallScore: 50,
    percentCompleted: "45%",
    bestContributor: "John",
  });

  const handleSubjectChange = (value: string) => {
    setSelectedSubject(value);
    filterStatCards(value, selectedPeriod);
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    if (value === "Custom") {
      setCustomDateRange([dayjs().subtract(7, "day"), dayjs()]);
    } else {
      setCustomDateRange([null, null]);
      filterStatCards(selectedSubject, value);
    }
  };

  const handleDateChange = (dates: null | [Dayjs | null | undefined, Dayjs | null | undefined]) => {
    if (dates){
      setCustomDateRange(dates);
      filterStatCards(selectedSubject, selectedPeriod, dates);
    }
    else{
      setCustomDateRange(null);
      filterStatCards(selectedSubject, selectedPeriod);
    }
  };

  const filterStatCards = (
    subject: string,
    period: string,
    dateRange?: null | [Dayjs | null | undefined, Dayjs | null | undefined]
  ) => {
    let filteredCompletedItems = 55;
    let filteredHoursCompleted = "40";
    let filteredOverallScore = 50;
    let filteredBestContributor = "John";
  };

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ];

  return (
    <div className="dashboard ">
      <Row gutter={[16, 16]}>
        <Col className="gutter-row" xs={24} sm={12} md={8} lg={8} xl={6}>
          <div className="filter-select-grp">
            <label className="filter-select-label" htmlFor="">
              Subject
            </label>
            <Select
              allowClear
              placeholder="Select Subjects"
              onChange={handleSubjectChange}
              className="filter-select"
              value={selectedSubject}
            >
              {subjectsOptions.map((subject) => (
                <Option key={subject} value={subject}>
                  {subject}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
        <Col className="gutter-row" xs={24} sm={12} md={8} lg={8} xl={6}>
          <div className="filter-select-grp date-select">
            <label className="filter-select-label" htmlFor="">
              Time period
            </label>
            <RangePicker 
              className="filter-select"
              presets={rangePresets}
              value={customDateRange}
              defaultValue={[dayjs().add(-7, 'd'), dayjs()]}
              onChange={handleDateChange}
              suffixIcon={null}
            />
          </div>
        </Col>
      </Row>

      <div className="counter-wrapper-block">
        <Row gutter={[65, 16]}>
          <Col className="gutter-row" xs={24} sm={12} md={8} lg={8} xl={6}>
            <SmallStatCard
              icon={CheckCircle}
              title="Items completed"
              mainStat={{
                type: "Text",
                content: filteredData.completedItems.toString(),
              }}
              secondaryStat={{ type: "Percent", value: 5 }}
            />
          </Col>
          <Col className="gutter-row" xs={24} sm={12} md={8} lg={8} xl={6}>
            <SmallStatCard
              icon={FieldTime}
              title="Hours completed"
              mainStat={{ type: "Text", content: filteredData.hoursCompleted }}
              secondaryStat={{ type: "Percent", value: 2 }}
            />
          </Col>
          <Col className="gutter-row" xs={24} sm={12} md={8} lg={8} xl={6}>
            <SmallStatCard
              icon={Vector}
              title="Percent completed"
              mainStat={{
                type: "Text",
                content: filteredData.percentCompleted,
              }}
              secondaryStat={{ type: "Percent", value: 45 }}
            />
          </Col>
          <Col className="gutter-row" xs={24} sm={12} md={8} lg={8} xl={6}>
            <SmallStatCard
              icon={User}
              title="Best contributor"
              mainStat={{ type: "Text", content: filteredData.bestContributor }}
              secondaryStat={{ type: "Hours", value: 102 }}
            />
          </Col>
        </Row>
      </div>
      <div className="counter-wrapper-block">
        <Row gutter={[65, 16]}>
          <Col className="gutter-row" xs={24} sm={24} md={12} lg={12}>
            <LargeStatCard
              title="Overall score"
              chartType="half-doughnut"
              dataset={{
                datasets: [
                  {
                    data: [75, 25],
                    backgroundColor: ["#3366cc", "#3b3eac"],
                    borderWidth: 0,
                  },
                ],
              }}
              centerText="75"
            />
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={12} lg={12}>
            <LargeStatCard
              title="Contributors"
              chartType="doughnut"
              dataset={{
                labels: ["John", "Joe", "Jay"],
                datasets: [
                  {
                    data: [60, 25, 15],
                    backgroundColor: ["#00B900", "#F4C401", "#FF0000"],
                    borderWidth: 0,
                  },
                ],
              }}
            />
          </Col>
        </Row>
      </div>
      <div className="counter-wrapper-block">
        <Row gutter={[16, 24]}>
          <Col
            className="gutter-row mb-3"
            xs={24}
            sm={24}
            md={12}
            lg={12}
            xl={8}
          >
            <div className="acState-overview">
              <ActivityCard
                title="Recent activity"
                link="/cms/activity"
                logs={logs}
              />
            </div>
          </Col>
          <Col
            className="gutter-row mb-3"
            xs={24}
            sm={24}
            md={12}
            lg={12}
            xl={8}
          >
            <div className="largeState-overview">
              <LargeStatCard
                title="Total subjects"
                chartType="number"
                dataset={50}
              />
            </div>
          </Col>
          <Col
            className="gutter-row mb-3"
            xs={24}
            sm={24}
            md={12}
            lg={12}
            xl={8}
          >
            <div className="acState-overview">
              <ActivityCard
                title="Notifications"
                link="/activity"
                logs={staticLogs}
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};
